import {
  Button,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { PlaylistAddRounded, UpdateRounded } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { CreateDocument, UpdateDocument } from '../../../api/documents';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  AimsDocumentType,
  getAimsDocumentTypesAsync
} from '../AimsDocumentType/aimsDocumentTypeSlice';
import { Discipline, getDisciplinesAsync } from '../Discipline/disciplineSlice';
import {
  DocumentGroup,
  getDocumentGroupsAsync
} from '../DocumentGroup/documentGroupSlice';
import {
  createDocumentAsync,
  getDocumentAsync,
  updateDocumentAsync
} from './documentSlice';

interface CreateEditDocument {
  id: number | null;
  no: string | null;
  name: string | null;
  discipline: Discipline | null;
  aimsDocumentType: AimsDocumentType | null;
  documentGroup: DocumentGroup | null;
  isActive: boolean;
}

export default function DocumentCreateEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { selectedDocument } = useSelector(
    (state: RootState) => state.documents
  );
  const { data: aimsDocumentTypes } = useSelector(
    (state: RootState) => state.aimsDocumentTypes
  );
  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );
  const { data: documentGroups } = useSelector(
    (state: RootState) => state.documentGroups
  );

  const params = useParams<{ documentId: string }>();
  const documentId = Number.parseInt(params.documentId, 10);
  const isNewPage = !Boolean(documentId);

  const [document, setDocument] = useState<CreateEditDocument>({
    id: null,
    no: null,
    name: null,
    discipline: null,
    aimsDocumentType: null,
    documentGroup: null,
    isActive: true
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedDocument) {
      setDocument({
        ...document,
        id: selectedDocument.id,
        name: selectedDocument.name,
        no: selectedDocument.no,
        discipline: selectedDocument.discipline,
        aimsDocumentType: selectedDocument.aimsDocumentType,
        documentGroup: selectedDocument.documentGroup,
        isActive: selectedDocument.isActive
      });
    }
  }, [selectedDocument]);

  useEffect(() => {
    if (!isNewPage) dispatch(getDocumentAsync(documentId));
    if (disciplines.length === 0) dispatch(getDisciplinesAsync());
    if (aimsDocumentTypes.length === 0) dispatch(getAimsDocumentTypesAsync());
    if (documentGroups.length === 0) dispatch(getDocumentGroupsAsync());
  }, []);

  function onChangeField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setDocument({
      ...document,
      [name]: value === '' ? null : value
    });
  }

  function handleChangeSwitch(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    setDocument({
      ...document,
      [event.target.name]: event.target.checked
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (
        document.name &&
        document.no &&
        document.discipline &&
        document.aimsDocumentType
      ) {
        if (isNewPage) {
          // create document
          const createDocument: CreateDocument = {
            name: document.name,
            no: document.no,
            disciplineId: document.discipline.id,
            aimsDocumentTypeId: document.aimsDocumentType.id,
            documentGroupId: document.documentGroup?.id ?? null
          };
          await dispatch(createDocumentAsync(createDocument)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('documentCreateEdit.messages.createSuccess')
            })
          );
        } else {
          // update document
          const updateDocument: UpdateDocument = {
            id: document.id,
            name: document.name,
            no: document.no,
            disciplineId: document.discipline.id,
            aimsDocumentTypeId: document.aimsDocumentType.id,
            documentGroupId: document.documentGroup?.id ?? null,
            isActive: document.isActive
          };
          await dispatch(updateDocumentAsync(updateDocument)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('documentCreateEdit.messages.updateSuccess')
            })
          );
        }
      } else {
        throw new Error(t('documentCreateEdit.errors.general') as string);
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('documentCreateEdit.breadcrumb.masterDataName'),
          masterDataRef: 'documents',
          masterDataSubName: selectedDocument?.name,
          masterDataSubRef: selectedDocument?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage ? t('documentCreateEdit.title.new') : t('documentCreateEdit.title.edit')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('documentCreateEdit.sections.information')}
          </Typography>
          <Divider className={classes.divider} />
          <div className={classes.row}>
            <TextField
              className={classes.columnLeft}
              id="document-name"
              label={t('documentCreateEdit.labels.name')}
              size="small"
              variant="outlined"
              name="name"
              fullWidth
              value={document.name ? document.name : ''}
              onChange={onChangeField}
              required
            />
            <TextField
              className={classes.columnRight}
              id="document-no"
              label={t('documentCreateEdit.labels.documentNo')}
              size="small"
              variant="outlined"
              name="no"
              fullWidth
              value={document.no ? document.no : ''}
              onChange={onChangeField}
              required
            />
          </div>
          <div className={classes.row}>
            <Autocomplete
              className={classes.columnLeft}
              id="discipline"
              size="small"
              options={disciplines}
              getOptionLabel={(option) => option?.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('documentCreateEdit.labels.discipline')}
                  required
                />
              )}
              value={document.discipline}
              onChange={(_, newValue) =>
                setDocument({
                  ...document,
                  discipline: newValue,
                  documentGroup: null
                })
              }
            />
            <Autocomplete
              className={classes.columnRight}
              id="aims-document-type"
              size="small"
              options={aimsDocumentTypes}
              getOptionLabel={(option) => option?.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('documentCreateEdit.labels.aimsDocumentType')}
                  required
                />
              )}
              value={document.aimsDocumentType}
              onChange={(_, newValue) =>
                setDocument({
                  ...document,
                  aimsDocumentType: newValue
                })
              }
            />
          </div>
          <Autocomplete
            id="document-group"
            size="small"
            options={documentGroups.filter(
              (documentGroup) =>
                documentGroup.disciplineId === document.discipline?.id
            )}
            getOptionLabel={(option) => option?.name}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={t('documentCreateEdit.labels.documentGroup')}
              />
            )}
            value={document.documentGroup}
            onChange={(_, newValue) =>
              setDocument({
                ...document,
                documentGroup: newValue
              })
            }
            disabled={!Boolean(document.discipline)}
            fullWidth
          />
          {!isNewPage && (
            <div className={classes.switchContainer}>
              <FormControlLabel
                control={
                  <Switch
                    checked={document.isActive}
                    onChange={handleChangeSwitch}
                    name="isActive"
                    color="primary"
                  />
                }
                label={t('documentCreateEdit.labels.activeDocument')}
              />
            </div>
          )}
        </Paper>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
          disabled={loading}
        >
          {isNewPage ? t('documentCreateEdit.buttons.add') : t('documentCreateEdit.buttons.update')}
        </Button>
      </form>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    },
    row: {
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    columnLeft: {
      width: '50%',
      marginRight: theme.spacing(0.5),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        marginRight: 0
      }
    },
    columnRight: {
      width: '50%',
      marginLeft: theme.spacing(0.5),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        marginLeft: 0
      }
    },
    switchContainer: {
      paddingTop: theme.spacing(1)
    }
  })
);
